// The Meta business profile of one WhatsApp account: photo, about, industry.
// Opened from the accounts screen; the data lives in Meta, not in our database.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";

import { ApiError, http } from "../api";
import {
  Banner,
  Dialog,
  DialogFooter,
  Field,
  SkeletonRows,
  Spinner,
  useToast,
} from "../ui";
import { errMessage } from "./shared";
import type { WhatsAppAccount } from "./accounts";

// ------------------------------------------------------------ business profile

interface BusinessProfile {
  messaging_product: string;
  address: string;
  description: string;
  vertical: string;
  email: string;
  websites: string[];
  profile_picture_url: string;
  about: string;
}

const EMPTY_PROFILE: BusinessProfile = {
  messaging_product: "whatsapp",
  address: "",
  description: "",
  vertical: "",
  email: "",
  websites: ["", ""],
  profile_picture_url: "",
  about: "",
};

const VERTICALS: { value: string; label: string }[] = [
  { value: "ALCOHOL", label: "Alcohol" },
  { value: "APPAREL", label: "Apparel" },
  { value: "AUTO", label: "Automotive" },
  { value: "BEAUTY", label: "Beauty & Personal Care" },
  { value: "EDU", label: "Education" },
  { value: "ENTERTAIN", label: "Entertainment" },
  { value: "EVENT_PLAN", label: "Event Planning" },
  { value: "FINANCE", label: "Finance & Banking" },
  { value: "GOVT", label: "Government & Public Service" },
  { value: "GROCERY", label: "Grocery" },
  { value: "HEALTH", label: "Health & Wellness" },
  { value: "HOTEL", label: "Hotel & Lodging" },
  { value: "NONPROFIT", label: "Non-profit" },
  { value: "ONLINE_GAMBLING", label: "Online Gambling" },
  { value: "OTC_DRUGS", label: "Over-the-counter Drugs" },
  { value: "OTHER", label: "Other/Not Listed" },
  { value: "PHYSICAL_GAMBLING", label: "Physical Gambling" },
  { value: "PROF_SERVICES", label: "Professional Services" },
  { value: "RETAIL", label: "Retail" },
  { value: "TRAVEL", label: "Travel & Transportation" },
];

const MAX_PROFILE_PHOTO = 5 * 1024 * 1024;

export function BusinessProfileDialog({
  account,
  onClose,
}: {
  account: WhatsAppAccount | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [profile, setProfile] = useState<BusinessProfile>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const accountId = account?.id ?? null;

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await http.get<Partial<BusinessProfile>>(`/accounts/${accountId}/business_profile`);
      const websites = (data.websites ?? []).length ? [...(data.websites ?? []), ""] : ["", ""];
      // Exactly two slots: Meta accepts at most two websites.
      while (websites.length < 2) websites.push("");
      setProfile({
        messaging_product: data.messaging_product || "whatsapp",
        address: data.address || "",
        description: data.description || "",
        vertical: data.vertical || "",
        email: data.email || "",
        websites: websites.slice(0, 2),
        profile_picture_url: data.profile_picture_url || "",
        about: data.about || "",
      });
    } catch (err) {
      setLoadError(err instanceof ApiError ? err : new ApiError(0, "client_error", String(err), ""));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  // Refetched on every open: the profile lives in Meta, not in our database.
  useEffect(() => {
    if (accountId) void load();
  }, [accountId, load]);

  const save = async () => {
    if (!accountId) return;
    setSaving(true);
    try {
      await http.put(`/accounts/${accountId}/business_profile`, {
        messaging_product: "whatsapp",
        address: profile.address,
        description: profile.description,
        vertical: profile.vertical,
        email: profile.email,
        websites: profile.websites.filter((w) => w.trim() !== ""),
        about: profile.about,
      });
      toast.push("Business profile updated", "good");
      onClose();
    } catch (err) {
      toast.push(errMessage(err, "Could not update the business profile"), "critical");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !accountId) return;
    if (!file.type.startsWith("image/")) {
      toast.push("Select an image file", "critical");
      return;
    }
    if (file.size > MAX_PROFILE_PHOTO) {
      toast.push("That image is larger than 5 MB", "critical");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await http.upload(`/accounts/${accountId}/business_profile/photo`, form);
      toast.push("Profile picture updated", "good");
      await load();
    } catch (err) {
      toast.push(errMessage(err, "Could not upload the profile picture"), "critical");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog
      open={!!account}
      onClose={onClose}
      title={`Business profile: ${account?.name ?? ""}`}
      wide
    >
      {loading ? (
        <SkeletonRows n={5} cols={2} />
      ) : (
        <>
          {loadError && (
            <Banner tone="critical" traceId={loadError.traceId}>
              Could not load the business profile: {loadError.message}
            </Banner>
          )}
          <Banner tone="warning">
            Profile changes are made directly in Meta and can take a few minutes to appear on the
            contact&apos;s device.
          </Banner>

          <div className="row" style={{ alignItems: "flex-start" }}>
            <button
              className="dropzone"
              style={{ width: 96, height: 96, padding: 0, borderRadius: "50%", overflow: "hidden" }}
              onClick={() => fileRef.current?.click()}
              aria-label="Change profile picture"
            >
              {uploading ? (
                <Spinner label="Uploading" />
              ) : profile.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span className="glyph">▣</span>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png, image/jpeg"
              className="sr-only"
              onChange={(e) => void onPhoto(e)}
            />
            <div className="text">
              <span className="strong">Profile picture</span>
              <p className="muted" style={{ fontSize: 12 }}>
                A square PNG or JPEG, 5 MB at most.
              </p>
            </div>
          </div>

          <Field label="About" hint={`${profile.about.length}/139`}>
            <input
              maxLength={139}
              value={profile.about}
              onChange={(e) => setProfile({ ...profile, about: e.target.value })}
            />
          </Field>

          <Field label="Business description" hint={`${profile.description.length}/512`}>
            <textarea
              rows={3}
              maxLength={512}
              value={profile.description}
              onChange={(e) => setProfile({ ...profile, description: e.target.value })}
            />
          </Field>

          <div className="grid c2">
            <Field label="Industry">
              <select
                value={profile.vertical}
                onChange={(e) => setProfile({ ...profile, vertical: e.target.value })}
              >
                <option value="">Select a category…</option>
                {VERTICALS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Contact email">
              <input
                type="email"
                maxLength={128}
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Business address">
            <input
              maxLength={256}
              value={profile.address}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
            />
          </Field>

          <Field label="Websites" hint="Up to two.">
            <div className="col">
              {profile.websites.map((site, i) => (
                <input
                  key={i}
                  maxLength={256}
                  value={site}
                  placeholder="https://example.com"
                  aria-label={`Website ${i + 1}`}
                  onChange={(e) => {
                    const next = [...profile.websites];
                    next[i] = e.target.value;
                    setProfile({ ...profile, websites: next });
                  }}
                />
              ))}
            </div>
          </Field>
        </>
      )}

      <DialogFooter>
        <button className="btn" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={saving || loading}>
          {saving && <Spinner label="Saving" />}
          Save changes
        </button>
      </DialogFooter>
    </Dialog>
  );
}
